import { createFileRoute } from '@tanstack/react-router'
import { LEGAL_CONFIG, LEGAL_VERSIONS } from '@/legal.config'
import { LegalVersionMeta } from '@/routes/legal/-legal-version-meta'

export const Route = createFileRoute('/legal/_layout/dpa')({
  component: DpaPage,
})

function DpaPage() {
  return (
    <article className="prose prose-neutral dark:prose-invert max-w-none">
      <h1>Data Processing Agreement</h1>
      <LegalVersionMeta
        current={LEGAL_VERSIONS.dpa.current}
        history={LEGAL_VERSIONS.dpa.history}
      />

      <p>
        This Data Processing Agreement ("DPA") forms part of the Terms of
        Service between {LEGAL_CONFIG.companyName} ("Processor") and the
        Customer ("Controller"), and governs the processing of personal data by
        the Processor on behalf of the Controller in connection with the{' '}
        {LEGAL_CONFIG.productName} service.
      </p>
      <p>
        This DPA is entered into pursuant to Article 28 of Regulation (EU)
        2016/679 (GDPR).
      </p>

      <h2>1. Definitions</h2>
      <ul>
        <li>
          <strong>"Controller"</strong> means the Customer organisation that
          determines the purposes and means of processing personal data of
          candidates and its own staff through the Service.
        </li>
        <li>
          <strong>"Processor"</strong> means {LEGAL_CONFIG.companyName}, which
          processes personal data on behalf of the Controller.
        </li>
        <li>
          <strong>"Personal Data"</strong> means any information relating to an
          identified or identifiable natural person processed in connection with
          the Service.
        </li>
        <li>
          <strong>"Sub-processor"</strong> means any third party engaged by the
          Processor to carry out processing activities on behalf of the
          Controller.
        </li>
        <li>
          Capitalised terms not defined here have the meanings given in the GDPR
          or the Terms of Service.
        </li>
      </ul>

      <h2>2. Scope and nature of processing</h2>
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Subject matter</td>
            <td>
              AI-assisted recruitment screening: interview recording,
              transcript, AI-generated competency scoring, and related candidate
              management
            </td>
          </tr>
          <tr>
            <td>Duration</td>
            <td>For the term of the Controller's subscription</td>
          </tr>
          <tr>
            <td>Nature of processing</td>
            <td>
              Collection, storage, analysis, structuring, disclosure (to
              authorised recruiters), restriction, and deletion
            </td>
          </tr>
          <tr>
            <td>Purpose</td>
            <td>
              To provide the Service as described in the Terms of Service,
              including AI-assisted interview assessment and recruiter review
              workflows
            </td>
          </tr>
          <tr>
            <td>Categories of data subjects</td>
            <td>
              Job candidates invited by the Controller; authorised users (staff)
              of the Controller
            </td>
          </tr>
          <tr>
            <td>Categories of personal data</td>
            <td>
              Name, email address, video recording, audio recording, chat
              transcript, AI-generated competency scores and rationale,
              recruiter actions and timestamps
            </td>
          </tr>
          <tr>
            <td>Special categories</td>
            <td>
              None intentionally processed. The Controller must not use the
              Service in a way that causes special-category data (e.g. health,
              biometric data used for identification) to be processed.
            </td>
          </tr>
        </tbody>
      </table>

      <h2>3. Processor obligations</h2>
      <p>The Processor shall:</p>
      <ol>
        <li>
          Process Personal Data only on documented instructions from the
          Controller, including as set out in this DPA and the Terms of Service,
          unless required to do so by Union or Member State law.
        </li>
        <li>
          Ensure that persons authorised to process Personal Data are bound by
          appropriate confidentiality obligations.
        </li>
        <li>
          Implement appropriate technical and organisational security measures
          as described in Section 6.
        </li>
        <li>
          Not engage sub-processors without the prior general written
          authorisation of the Controller (general authorisation is deemed given
          by acceptance of these Terms; specific changes are notified per
          Section 4).
        </li>
        <li>
          Assist the Controller in fulfilling its obligations to respond to data
          subject rights requests, including access, rectification, erasure, and
          portability, to the extent technically feasible.
        </li>
        <li>
          Assist the Controller with security obligations, breach notification,
          data protection impact assessments, and prior consultation under
          Articles 32–36 GDPR, taking into account the nature of the processing
          and information available to the Processor.
        </li>
        <li>
          At the Controller's choice, delete or return all Personal Data upon
          termination of the Service, and delete existing copies unless Union or
          Member State law requires storage of the Personal Data.
        </li>
        <li>
          Make available all information necessary to demonstrate compliance
          with Article 28 GDPR and allow for and contribute to audits and
          inspections conducted by the Controller or an auditor mandated by the
          Controller (on reasonable notice and at the Controller's cost).
        </li>
      </ol>

      <h2>4. Sub-processors</h2>
      <p>
        The Controller provides general authorisation for the Processor to
        engage sub-processors. The current named list, including processing
        activity, location, and transfer mechanism for each, is maintained at{' '}
        <a href="/legal/subprocessors">
          {LEGAL_CONFIG.websiteUrl}/legal/subprocessors
        </a>
        .
      </p>
      <p>
        The Processor will notify the Controller of any intended additions or
        replacements at least 14 days in advance. The Controller may object in
        writing within 14 days; failing which, the change is deemed accepted.
        The Processor maintains executed Standard Contractual Clauses
        (Commission Decision 2021/914/EU) with each sub-processor where
        required. Copies are available on request.
      </p>

      <h2>5. Controller obligations</h2>
      <p>The Controller shall:</p>
      <ol>
        <li>
          Ensure it has a valid lawful basis under GDPR for directing the
          Processor to process Personal Data.
        </li>
        <li>
          Inform candidates about the AI-assisted nature of interviews and their
          rights before the interview begins. The Service provides a mandatory
          transparency notice for this purpose.
        </li>
        <li>
          Not instruct the Processor to process data in a way that would violate
          applicable law, including anti-discrimination law.
        </li>
        <li>
          Conduct and document a Fundamental Rights Impact Assessment (FRIA)
          where required under EU AI Act Art. 27.
        </li>
        <li>
          Maintain human oversight over all hiring decisions and not use
          AI-generated scores as the sole basis for any decision.
        </li>
      </ol>

      <h2>6. Security measures</h2>
      <p>
        The Processor implements and maintains the following technical and
        organisational measures:
      </p>
      <ul>
        <li>Encryption of Personal Data in transit (TLS 1.2+)</li>
        <li>Encryption of Personal Data at rest (AES-256)</li>
        <li>
          Access controls: role-based access, principle of least privilege,
          authentication required for all dashboard access
        </li>
        <li>
          Interview recordings stored in private, non-publicly-accessible object
          storage with time-limited signed URLs
        </li>
        <li>
          AI audit logging of all model runs and recruiter actions (model
          version, prompt version, timestamps)
        </li>
        <li>Automated error monitoring (Sentry)</li>
        <li>Regular dependency security reviews</li>
      </ul>

      <h2>7. Personal data breach notification</h2>
      <p>
        The Processor shall notify the Controller without undue delay (and in
        any event within 72 hours of becoming aware) of any Personal Data breach
        affecting data processed under this DPA, providing sufficient
        information to allow the Controller to meet its notification obligations
        to supervisory authorities and data subjects.
      </p>

      <h2>8. Deletion and return of data</h2>
      <p>
        Upon termination of the Service or written request by the Controller,
        the Processor shall, at the Controller's choice:
      </p>
      <ul>
        <li>
          Delete all Personal Data within 30 days, except where retention is
          required by law; or
        </li>
        <li>
          Return a machine-readable export of Personal Data to the Controller
          within 30 days.
        </li>
      </ul>
      <p>
        AI audit log entries are retained for a minimum of{' '}
        {LEGAL_CONFIG.auditLogRetentionDays} days after deletion to comply with
        EU AI Act Art. 12. These entries are anonymised to remove candidate PII
        at the time of erasure.
      </p>

      <h2>9. Audits</h2>
      <p>
        The Controller may conduct an audit of the Processor's data processing
        practices no more than once per calendar year, on 30 days' written
        notice, during business hours, and at the Controller's expense. The
        Processor may satisfy audit requests by providing third-party audit
        reports (e.g. SOC 2) in lieu of on-site inspections.
      </p>

      <h2>10. Governing law</h2>
      <p>
        This DPA is governed by {LEGAL_CONFIG.companyJurisdiction} and is
        subject to the jurisdiction and dispute resolution provisions of the
        Terms of Service.
      </p>

      <h2>11. Order of precedence</h2>
      <p>
        In the event of a conflict between this DPA and the Terms of Service on
        matters relating to data protection, this DPA prevails.
      </p>

      <h2>12. Contact</h2>
      <p>
        Data protection enquiries:{' '}
        <a href={`mailto:${LEGAL_CONFIG.privacyEmail}`}>
          {LEGAL_CONFIG.privacyEmail}
        </a>
        <br />
        {LEGAL_CONFIG.companyName}
        <br />
        {LEGAL_CONFIG.companyAddress}
      </p>
    </article>
  )
}
